import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import { server } from "../../server";
import { fetchGatePassDetails } from "../../redux/gatePassSlice";

/**
 * FrontOfficeReports
 *
 * Mirrors StudentReports / FinanceReports layout exactly:
 *  Left col  → Hero + Dataset selector + Filters + Live Snapshot
 *  Right col → Report panel (category tabs + report buttons + quick action cards)
 *
 * Datasets:
 *  1. Visitor Book      – visitor entries        (VisitorBook)
 *  2. Admission Enquiry – admission enquiries    (AdmissionEnquiry)
 *  3. Complain Box      – complaints             (ComplainBox)
 *  4. Gate Pass         – student gate passes    (GatePass)
 *  5. Medical Alerts    – student medical alerts (MedicalAlert)
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (wb, filename) => {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  toast.success("Report Generated");
};

// ─── dataset meta ─────────────────────────────────────────────────────────────
const DATASETS = {
  visitor: { label: "Visitor Book", color: "success" },
  enquiry: { label: "Admission Enquiry", color: "warning" },
  complain: { label: "Complain Box", color: "danger" },
  gatepass: { label: "Gate Pass", color: "info" },
  medical: { label: "Medical Alerts", color: "warning" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "visitor", label: "Visitor" },
  { key: "enquiry", label: "Enquiry" },
  { key: "complain", label: "Complaints" },
  { key: "gatepass", label: "Gate Pass" },
  { key: "medical", label: "Medical" },
  { key: "analytics", label: "Analytics" },
];

// ─────────────────────────────────────────────────────────────────────────────
function FrontOfficeReports() {
  const dispatch = useDispatch();

  // Gate pass from redux (GatePass uses redux)
  const { data: gatePassDetails, loading: loadingGatePass } = useSelector(
    (state) => state.gatePass,
  );

  // ── local data ─────────────────────────────────────────────────────────────
  const [visitorData, setVisitorData] = useState([]);
  const [enquiryData, setEnquiryData] = useState([]);
  const [complainData, setComplainData] = useState([]);
  const [medicalData, setMedicalData] = useState([]);

  const [loadingVisitor, setLoadingVisitor] = useState(false);
  const [loadingEnquiry, setLoadingEnquiry] = useState(false);
  const [loadingComplain, setLoadingComplain] = useState(false);
  const [loadingMedical, setLoadingMedical] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("visitor");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPurpose, setSelectedPurpose] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── fetches ────────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchGatePassDetails());

    // Visitor book
    setLoadingVisitor(true);
    fetch(`${server}/getAllVisitorBook_mongodb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const data = d?.["body-json"]?.body?.data || d?.data || [];
        setVisitorData(
          Array.isArray(data)
            ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            : [],
        );
      })
      .catch(console.error)
      .finally(() => setLoadingVisitor(false));

    // Admission enquiry
    setLoadingEnquiry(true);
    fetch(`${server}/all_enquiry_mongodb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const data = d?.["body-json"]?.body?.data || d?.data || [];
        setEnquiryData(Array.isArray(data) ? data : []);
      })
      .catch(console.error)
      .finally(() => setLoadingEnquiry(false));

    // Complain box
    setLoadingComplain(true);
    fetch(`${server}/complainBox_alldata_mongodb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const data = d?.["body-json"]?.body?.data || [];
        setComplainData(
          Array.isArray(data)
            ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            : [],
        );
      })
      .catch(console.error)
      .finally(() => setLoadingComplain(false));

    // Medical alerts
    setLoadingMedical(true);
    fetch(`${server}/GetAllMedicalAlerts`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const data = d?.data || d?.alerts || d?.["body-json"]?.body?.data || [];
        setMedicalData(Array.isArray(data) ? data : []);
      })
      .catch(console.error)
      .finally(() => setLoadingMedical(false));
  }, [dispatch]);

  // ── dataset map ────────────────────────────────────────────────────────────
  const gatePassArr = gatePassDetails || [];

  const datasetMap = {
    visitor: { data: visitorData, loading: loadingVisitor },
    enquiry: { data: enquiryData, loading: loadingEnquiry },
    complain: { data: complainData, loading: loadingComplain },
    gatepass: { data: gatePassArr, loading: loadingGatePass },
    medical: { data: medicalData, loading: loadingMedical },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading || false;

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedPurpose) {
      data = data.filter((r) =>
        (r.purpose || r.source || r.complainType || r.category || "")
          .toLowerCase()
          .includes(selectedPurpose.toLowerCase()),
      );
    }

    if (selectedStatus) {
      data = data.filter(
        (r) => (r.status || "").toLowerCase() === selectedStatus.toLowerCase(),
      );
    }

    if (fromDate || toDate) {
      data = data.filter((r) => {
        const d = (r.createdAt || r.date || "")?.slice(0, 10);
        if (!d) return false;
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((r) =>
        flatten(r).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [
    currentRaw,
    selectedPurpose,
    selectedStatus,
    fromDate,
    toDate,
    searchTerm,
  ]);

  // ── live snapshot ──────────────────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const todayVisitors = visitorData.filter(
      (v) => v.createdAt?.slice(0, 10) === today,
    ).length;
    const openComplaints = complainData.filter(
      (c) => (c.status || "").toLowerCase() !== "solved",
    ).length;
    const pendingPasses = gatePassArr.filter(
      (g) => (g.status || "").toLowerCase() === "pending",
    ).length;
    const activeEnquiry = enquiryData.filter(
      (e) => (e.status || "active").toLowerCase() === "active",
    ).length;
    const callParents = medicalData.filter(
      (m) => m.callParents === true,
    ).length;

    return {
      totalVisitors: visitorData.length,
      todayVisitors,
      totalEnquiry: enquiryData.length,
      activeEnquiry,
      totalComplaints: complainData.length,
      openComplaints,
      totalPasses: gatePassArr.length,
      pendingPasses,
      totalMedical: medicalData.length,
      callParents,
    };
  }, [visitorData, enquiryData, complainData, gatePassArr, medicalData]);

  // ── select styles (identical to all report pages) ─────────────────────────
  const selectStyles = {
    control: (b) => ({
      ...b,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (b) => ({ ...b, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
    menu: (b) => ({
      ...b,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? "#ffc107"
        : s.isFocused
          ? "#f8f9fa"
          : "white",
      color: s.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (b) => ({ ...b, color: "#6c757d", fontSize: "14px" }),
    singleValue: (b) => ({ ...b, color: "#212529", fontSize: "14px" }),
  };

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "solved", label: "Solved" },
    { value: "active", label: "Active" },
    { value: "under review", label: "Under Review" },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  //  ROW FORMATTERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatVisitorRow = (v) => ({
    "VISITOR ID": toUpper(v.visitorId || ""),
    NAME: toUpper(v.name || ""),
    "PHONE NO": toUpper(v.phoneNo || ""),
    "EMAIL ID": v.emailId || "",
    "VISITOR TYPE": toUpper(v.visitorType || ""),
    PURPOSE: toUpper(v.purpose || ""),
    "MEETING WITH": toUpper(v.meetingWith || ""),
    "NO. OF PERSONS": String(v.numberOfPersons || ""),
    "ADMISSION NO": toUpper(v.admissionNo || ""),
    "IN TIME": toUpper(v.inTime || ""),
    "OUT TIME": toUpper(v.outTime || ""),
    REMARK: v.remark || "",
    DATE: v.createdAt ? new Date(v.createdAt).toLocaleDateString("en-GB") : "",
  });

  const formatEnquiryRow = (e) => ({
    NAME: toUpper(e.name || ""),
    CLASS: toUpper(e.class || ""),
    "PHONE NO": toUpper(e.phoneNo || ""),
    EMAIL: e.email || "",
    SOURCE: toUpper(e.source || ""),
    STATUS: toUpper(e.status || ""),
    REMARK: e.remark || "",
    DATE: e.date ? new Date(e.date).toLocaleDateString("en-GB") : "",
    "NEXT FOLLOW UP": e.nextFollowUpDate
      ? new Date(e.nextFollowUpDate).toLocaleDateString("en-GB")
      : "",
  });

  const formatComplainRow = (c) => ({
    "COMPLAIN ID": toUpper(c.complainId || ""),
    "COMPLAIN TYPE": toUpper(c.complainType || ""),
    NAME: toUpper(c.name || ""),
    "PHONE NO": toUpper(c.phoneNo || ""),
    DESCRIPTION: c.complainDescription || "",
    STATUS: toUpper(c.status || ""),
    DATE: c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-GB") : "",
  });

  const formatGatePassRow = (g) => ({
    "PASS ID": toUpper(g.gatePassId || g._id || ""),
    "ADMISSION NO": Array.isArray(g.admission_no)
      ? g.admission_no.join(", ")
      : toUpper(g.admission_no || ""),
    PURPOSE: toUpper(g.purpose || ""),
    "OUT TIME": toUpper(g.outTime || ""),
    STATUS: toUpper(g.status || ""),
    REMARK: g.remark || "",
    DATE: g.createdAt ? new Date(g.createdAt).toLocaleDateString("en-GB") : "",
  });

  const formatMedicalRow = (m) => ({
    "ADMISSION NO": toUpper(m.admission_no || ""),
    "STUDENT NAME": toUpper(m.name || m.studentName || ""),
    CATEGORY: toUpper(m.category || ""),
    CONDITION: toUpper(m.condition || ""),
    MEDICINE: Array.isArray(m.medicine)
      ? m.medicine.join(", ")
      : toUpper(m.medicine || ""),
    REMARK: m.remark || "",
    "CALL PARENTS": m.callParents ? "YES" : "NO",
    DATE: m.createdAt ? new Date(m.createdAt).toLocaleDateString("en-GB") : "",
  });

  // map dataset → formatter
  const getFormatter = () => {
    switch (activeDataset) {
      case "visitor":
        return formatVisitorRow;
      case "enquiry":
        return formatEnquiryRow;
      case "complain":
        return formatComplainRow;
      case "gatepass":
        return formatGatePassRow;
      case "medical":
        return formatMedicalRow;
      default:
        return (r) => r;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  GENERIC REPORT GENERATORS
  // ─────────────────────────────────────────────────────────────────────────
  const reportAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(filteredData.map(getFormatter())),
      DATASETS[activeDataset].label.slice(0, 31),
    );
    writeWorkbook(
      wb,
      `FrontOffice_${DATASETS[activeDataset].label.replace(/ /g, "_")}_${today}.xlsx`,
    );
  };

  const reportGroupWise = (data, groupFn, filename, formatter) => {
    if (!data.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    data.forEach((r) => {
      const key = toUpper(groupFn(r) || "UNKNOWN");
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatter(r));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        key.slice(0, 31),
      );
    });
    writeWorkbook(wb, `${filename}_${today}.xlsx`);
  };

  const reportToday = (data, formatter, label) => {
    const list = data.filter(
      (r) => (r.createdAt || r.date || "")?.slice(0, 10) === today,
    );
    if (!list.length) {
      toast.error(`No ${label} today`);
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatter)),
      `TODAY`,
    );
    writeWorkbook(wb, `${label}_Today_${today}.xlsx`);
  };

  const reportDateRange = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From and To dates");
      return;
    }
    if (!filteredData.length) {
      toast.error("No records in this date range");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(filteredData.map(getFormatter())),
      "DATE RANGE",
    );
    writeWorkbook(wb, `FrontOffice_${fromDate}_to_${toDate}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  VISITOR REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportVisitorAll = () => {
    if (!visitorData.length) {
      toast.error("No visitor data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(visitorData.map(formatVisitorRow)),
      "ALL VISITORS",
    );
    writeWorkbook(wb, `Visitor_All_${today}.xlsx`);
  };
  const reportVisitorToday = () =>
    reportToday(visitorData, formatVisitorRow, "Visitor");
  const reportVisitorPurpose = () =>
    reportGroupWise(
      visitorData,
      (v) => v.purpose,
      "Visitor_PurposeWise",
      formatVisitorRow,
    );
  const reportVisitorType = () =>
    reportGroupWise(
      visitorData,
      (v) => v.visitorType,
      "Visitor_TypeWise",
      formatVisitorRow,
    );
  const reportVisitorWithout = () => {
    const list = visitorData.filter(
      (v) => !v.outTime || v.outTime.trim() === "",
    );
    if (!list.length) {
      toast.error("No visitors still inside");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatVisitorRow)),
      "STILL INSIDE",
    );
    writeWorkbook(wb, `Visitor_StillInside_${today}.xlsx`);
  };
  const reportVisitorPickup = () => {
    const list = visitorData.filter((v) =>
      (v.purpose || "").toLowerCase().includes("pick-up"),
    );
    if (!list.length) {
      toast.error("No student pick-up records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatVisitorRow)),
      "STUDENT PICKUP",
    );
    writeWorkbook(wb, `Visitor_StudentPickup_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ENQUIRY REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportEnquiryAll = () => {
    if (!enquiryData.length) {
      toast.error("No enquiry data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(enquiryData.map(formatEnquiryRow)),
      "ALL ENQUIRIES",
    );
    writeWorkbook(wb, `Enquiry_All_${today}.xlsx`);
  };
  const reportEnquiryToday = () =>
    reportToday(enquiryData, formatEnquiryRow, "Enquiry");
  const reportEnquirySource = () =>
    reportGroupWise(
      enquiryData,
      (e) => e.source,
      "Enquiry_SourceWise",
      formatEnquiryRow,
    );
  const reportEnquiryClass = () =>
    reportGroupWise(
      enquiryData,
      (e) => e.class,
      "Enquiry_ClassWise",
      formatEnquiryRow,
    );
  const reportEnquiryStatus = () =>
    reportGroupWise(
      enquiryData,
      (e) => e.status,
      "Enquiry_StatusWise",
      formatEnquiryRow,
    );
  const reportEnquiryFollowup = () => {
    const list = enquiryData.filter((e) => {
      const fu = e.nextFollowUpDate
        ? new Date(e.nextFollowUpDate).toISOString().slice(0, 10)
        : "";
      return fu === today;
    });
    if (!list.length) {
      toast.error("No follow-ups due today");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatEnquiryRow)),
      "FOLLOW-UP TODAY",
    );
    writeWorkbook(wb, `Enquiry_FollowupToday_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  COMPLAIN REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportComplainAll = () => {
    if (!complainData.length) {
      toast.error("No complain data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(complainData.map(formatComplainRow)),
      "ALL COMPLAINTS",
    );
    writeWorkbook(wb, `Complain_All_${today}.xlsx`);
  };
  const reportComplainType = () =>
    reportGroupWise(
      complainData,
      (c) => c.complainType,
      "Complain_TypeWise",
      formatComplainRow,
    );
  const reportComplainStatus = () =>
    reportGroupWise(
      complainData,
      (c) => c.status,
      "Complain_StatusWise",
      formatComplainRow,
    );
  const reportComplainOpen = () => {
    const list = complainData.filter(
      (c) => (c.status || "").toLowerCase() !== "solved",
    );
    if (!list.length) {
      toast.error("No open complaints");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatComplainRow)),
      "OPEN",
    );
    writeWorkbook(wb, `Complain_Open_${today}.xlsx`);
  };
  const reportComplainSolved = () => {
    const list = complainData.filter(
      (c) => (c.status || "").toLowerCase() === "solved",
    );
    if (!list.length) {
      toast.error("No solved complaints");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatComplainRow)),
      "SOLVED",
    );
    writeWorkbook(wb, `Complain_Solved_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  GATE PASS REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportGatePassAll = () => {
    if (!gatePassArr.length) {
      toast.error("No gate pass data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(gatePassArr.map(formatGatePassRow)),
      "ALL PASSES",
    );
    writeWorkbook(wb, `GatePass_All_${today}.xlsx`);
  };
  const reportGatePassToday = () =>
    reportToday(gatePassArr, formatGatePassRow, "GatePass");
  const reportGatePassStatus = () =>
    reportGroupWise(
      gatePassArr,
      (g) => g.status,
      "GatePass_StatusWise",
      formatGatePassRow,
    );
  const reportGatePassPending = () => {
    const list = gatePassArr.filter(
      (g) => (g.status || "").toLowerCase() === "pending",
    );
    if (!list.length) {
      toast.error("No pending gate passes");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatGatePassRow)),
      "PENDING",
    );
    writeWorkbook(wb, `GatePass_Pending_${today}.xlsx`);
  };
  const reportGatePassPurpose = () =>
    reportGroupWise(
      gatePassArr,
      (g) => g.purpose,
      "GatePass_PurposeWise",
      formatGatePassRow,
    );

  // ─────────────────────────────────────────────────────────────────────────
  //  MEDICAL REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportMedicalAll = () => {
    if (!medicalData.length) {
      toast.error("No medical alert data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(medicalData.map(formatMedicalRow)),
      "ALL ALERTS",
    );
    writeWorkbook(wb, `Medical_All_${today}.xlsx`);
  };
  const reportMedicalCategory = () =>
    reportGroupWise(
      medicalData,
      (m) => m.category,
      "Medical_CategoryWise",
      formatMedicalRow,
    );
  const reportMedicalCallParent = () => {
    const list = medicalData.filter((m) => m.callParents === true);
    if (!list.length) {
      toast.error("No records requiring parent contact");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatMedicalRow)),
      "CALL PARENTS",
    );
    writeWorkbook(wb, `Medical_CallParents_${today}.xlsx`);
  };
  const reportMedicalCondition = () =>
    reportGroupWise(
      medicalData,
      (m) => m.condition,
      "Medical_ConditionWise",
      formatMedicalRow,
    );

  // ─────────────────────────────────────────────────────────────────────────
  //  ANALYTICS
  // ─────────────────────────────────────────────────────────────────────────
  const reportFrontOfficeSummary = () => {
    const wb = XLSX.utils.book_new();
    const summary = [
      {
        MODULE: "Visitor Book",
        TOTAL: visitorData.length,
        TODAY: visitorData.filter((v) => v.createdAt?.slice(0, 10) === today)
          .length,
        NOTE: "Visitors logged",
      },
      {
        MODULE: "Admission Enquiry",
        TOTAL: enquiryData.length,
        TODAY: enquiryData.filter((e) => e.date?.slice(0, 10) === today).length,
        NOTE: `${snapshot.activeEnquiry} active`,
      },
      {
        MODULE: "Complain Box",
        TOTAL: complainData.length,
        TODAY: complainData.filter((c) => c.createdAt?.slice(0, 10) === today)
          .length,
        NOTE: `${snapshot.openComplaints} open`,
      },
      {
        MODULE: "Gate Pass",
        TOTAL: gatePassArr.length,
        TODAY: gatePassArr.filter((g) => g.createdAt?.slice(0, 10) === today)
          .length,
        NOTE: `${snapshot.pendingPasses} pending`,
      },
      {
        MODULE: "Medical Alerts",
        TOTAL: medicalData.length,
        TODAY: medicalData.filter((m) => m.createdAt?.slice(0, 10) === today)
          .length,
        NOTE: `${snapshot.callParents} call parents`,
      },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(summary),
      "Summary",
    );
    writeWorkbook(wb, `FrontOffice_Summary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    const wb = XLSX.utils.book_new();
    if (visitorData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(visitorData.map(formatVisitorRow)),
        "Visitors",
      );
    if (enquiryData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(enquiryData.map(formatEnquiryRow)),
        "Enquiries",
      );
    if (complainData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(complainData.map(formatComplainRow)),
        "Complaints",
      );
    if (gatePassArr.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(gatePassArr.map(formatGatePassRow)),
        "Gate Passes",
      );
    if (medicalData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(medicalData.map(formatMedicalRow)),
        "Medical Alerts",
      );
    if (!wb.SheetNames.length) {
      toast.error("No data available");
      return;
    }
    writeWorkbook(wb, `FrontOffice_MasterReport_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT DEFINITIONS LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // Visitor
    { cat: "visitor", label: "All Visitors", action: reportVisitorAll },
    { cat: "visitor", label: "Today's Visitors", action: reportVisitorToday },
    { cat: "visitor", label: "Purpose Wise", action: reportVisitorPurpose },
    { cat: "visitor", label: "Visitor Type Wise", action: reportVisitorType },
    { cat: "visitor", label: "Still Inside", action: reportVisitorWithout },
    { cat: "visitor", label: "Student Pick-Up", action: reportVisitorPickup },
    { cat: "visitor", label: "Date Range", action: reportDateRange },
    // Enquiry
    { cat: "enquiry", label: "All Enquiries", action: reportEnquiryAll },
    { cat: "enquiry", label: "Today's Enquiries", action: reportEnquiryToday },
    { cat: "enquiry", label: "Source Wise", action: reportEnquirySource },
    { cat: "enquiry", label: "Class Wise", action: reportEnquiryClass },
    { cat: "enquiry", label: "Status Wise", action: reportEnquiryStatus },
    { cat: "enquiry", label: "Follow-Up Today", action: reportEnquiryFollowup },
    // Complaints
    { cat: "complain", label: "All Complaints", action: reportComplainAll },
    {
      cat: "complain",
      label: "Complaint Type Wise",
      action: reportComplainType,
    },
    { cat: "complain", label: "Status Wise", action: reportComplainStatus },
    { cat: "complain", label: "Open Complaints", action: reportComplainOpen },
    {
      cat: "complain",
      label: "Solved Complaints",
      action: reportComplainSolved,
    },
    // Gate Pass
    { cat: "gatepass", label: "All Gate Passes", action: reportGatePassAll },
    { cat: "gatepass", label: "Today's Passes", action: reportGatePassToday },
    { cat: "gatepass", label: "Status Wise", action: reportGatePassStatus },
    { cat: "gatepass", label: "Pending Passes", action: reportGatePassPending },
    { cat: "gatepass", label: "Purpose Wise", action: reportGatePassPurpose },
    // Medical
    { cat: "medical", label: "All Medical Alerts", action: reportMedicalAll },
    { cat: "medical", label: "Category Wise", action: reportMedicalCategory },
    { cat: "medical", label: "Condition Wise", action: reportMedicalCondition },
    {
      cat: "medical",
      label: "Requires Parent Call",
      action: reportMedicalCallParent,
    },
    // Analytics
    {
      cat: "analytics",
      label: "Front Office Summary",
      action: reportFrontOfficeSummary,
    },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              {/* ══════════════ LEFT COLUMN ══════════════ */}
              <div className="col-lg-8">
                {/* ── HERO HEADER ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-door-open me-1"></i> Reports &amp;
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Front Office Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every front office
                          report — all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── FILTERS ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedPurpose("");
                        setSelectedStatus("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search name, phone, purpose, type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Purpose / Type & Status */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Purpose / Type &amp; Status
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-tag-fill text-secondary me-2"></i>
                          <input
                            type="text"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            placeholder="Filter by purpose / type..."
                            value={selectedPurpose}
                            onChange={(e) => setSelectedPurpose(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i
                            className="bi bi-circle-fill text-secondary me-2"
                            style={{ fontSize: "8px" }}
                          ></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Statuses"
                            value={
                              selectedStatus
                                ? {
                                    value: selectedStatus,
                                    label:
                                      selectedStatus.charAt(0).toUpperCase() +
                                      selectedStatus.slice(1),
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedStatus(opt ? opt.value : "")
                            }
                            options={statusOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── LIVE SNAPSHOT ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut — visitors vs enquiries */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.totalVisitors + snapshot.totalEnquiry ? (snapshot.totalVisitors / (snapshot.totalVisitors + snapshot.totalEnquiry)) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#ffc107"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.totalVisitors + snapshot.totalEnquiry ? (snapshot.totalEnquiry / (snapshot.totalVisitors + snapshot.totalEnquiry)) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${snapshot.totalVisitors + snapshot.totalEnquiry ? -(snapshot.totalVisitors / (snapshot.totalVisitors + snapshot.totalEnquiry)) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">
                            Records
                          </p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {filteredData.length.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-walking text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Visitors Today
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.todayVisitors.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.totalVisitors} total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-question-circle-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Enquiries
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.totalEnquiry.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.activeEnquiry} active
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-danger-soft rounded-4 p-4 h-100">
                            <i className="bi bi-exclamation-triangle-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Open Complaints
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.openComplaints.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.totalComplaints} total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-pass-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Pending Passes
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.pendingPasses.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.totalPasses} total
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ══════════════ RIGHT COLUMN ══════════════ */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Front Office
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#foReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="foReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="foReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards — Summary */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportFrontOfficeSummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Front Office
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Modules
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportVisitorToday}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Visitors
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Today</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportComplainOpen}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Open
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Complaints
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default FrontOfficeReports;
